/**
 * Centralized notification helper utilities
 *
 * This module consolidates all notification state-checking logic that was
 * previously duplicated across multiple files:
 * - NotificationProvider.tsx
 * - NotificationsDrawer.tsx
 * - ProtectedLayout/index.tsx
 * - WorkflowAssistant.tsx
 *
 * Single source of truth for:
 * - State categorization (completed, processing, failed, pending)
 * - Notification deduplication keys
 * - Active/inactive notification detection
 */

import { IWorkflowNotification } from '@/types/plays';

/**
 * Status categories for notifications
 */
type NotificationStatusCategory = 'completed' | 'processing' | 'failed' | 'pending';
type NotificationVisibility = 'user' | 'technical';

/**
 * States that indicate a notification is in a final/completed state
 * These notifications should NOT count toward active notification badge
 */
const COMPLETED_STATES = [
  'completed',
  'success',
  'email_sent',
  'email_drafted',
  'test_email_generated',
  'test_video_generated',
  'skipped',
  'scheduled',
] as const;

/**
 * States that indicate a notification has failed
 */
const FAILED_STATES = [
  'failed',
  'error',
  'cancelled',
] as const;

/**
 * States that indicate a notification is actively processing
 */
const PROCESSING_STATES = [
  'processing',
  'running',
  'started',
  'in_progress',
  'generating_content',
] as const;

/**
 * Check if a state string matches any of the target states (case-insensitive)
 */
const matchesAnyState = (state: string, targets: readonly string[]): boolean => {
  const lowerState = state.toLowerCase();
  return targets.some(target => lowerState.includes(target.toLowerCase()));
};

/**
 * Determines the status category of a notification
 *
 * Uses standardized.status if available, falls back to raw state.
 * Returns one of: 'completed' | 'processing' | 'failed' | 'pending'
 *
 * @param notification - The workflow notification to categorize
 * @returns The status category
 */
const getStatusCategory = (notification: IWorkflowNotification): NotificationStatusCategory => {
  const status = notification.standardized?.status || notification.state || '';
  const lowerStatus = status.toLowerCase();

  if (matchesAnyState(lowerStatus, COMPLETED_STATES)) {
    return 'completed';
  }
  if (matchesAnyState(lowerStatus, FAILED_STATES)) {
    return 'failed';
  }
  if (matchesAnyState(lowerStatus, PROCESSING_STATES)) {
    return 'processing';
  }
  // Default to pending for unknown states (queued, pending, etc.)
  return 'pending';
};

/**
 * Check if a notification is in a completed/final state
 *
 * @param notification - The workflow notification to check
 * @returns True if the notification is completed (success or failure)
 */
export const isCompletedNotification = (notification: IWorkflowNotification): boolean => {
  const category = getStatusCategory(notification);
  return category === 'completed' || category === 'failed';
};

/**
 * Check if a notification is in an active (non-final) state
 *
 * Active notifications are those still in progress or pending.
 * Used for counting badge numbers.
 *
 * @param notification - The workflow notification to check
 * @returns True if the notification is active (processing or pending)
 */
export const isActiveNotification = (notification: IWorkflowNotification): boolean => {
  return !isCompletedNotification(notification);
};

/**
 * Check if a notification is in a failed state
 *
 * @param notification - The workflow notification to check
 * @returns True if the notification represents a failure
 */
const isFailedNotification = (notification: IWorkflowNotification): boolean => {
  return getStatusCategory(notification) === 'failed';
};

/**
 * Check if a notification is in a processing state
 *
 * @param notification - The workflow notification to check
 * @returns True if the notification is actively processing
 */
const isProcessingNotification = (notification: IWorkflowNotification): boolean => {
  return getStatusCategory(notification) === 'processing';
};

/**
 * Generate a unique key for a notification for deduplication
 *
 * Uses execution ID if available (most reliable), otherwise falls back
 * to a composite key of audience, worker, state, and timestamp.
 *
 * @param notification - The workflow notification
 * @returns A unique string key for the notification
 */
const getNotificationKey = (notification: IWorkflowNotification): string => {
  const executionId = notification.executionId
    || notification.raw?.executionId
    || notification.raw?.execution_id
    || notification.raw?.play_execution_id;
  const messageId = notification.raw?.messageId || notification.raw?.message_id;
  const audiencePlayId = notification.audiencePlayId || notification.raw?.audiencePlayId || notification.raw?.audience_play_id;
  const recipientEmail = notification.raw?.recipientEmail || notification.raw?.recipient_email;

  if (executionId) {
    return `exec_${executionId}_${notification.state}`;
  }
  if (messageId) {
    return `msg_${messageId}_${notification.state}`;
  }
  if (audiencePlayId) {
    return `play_${audiencePlayId}_${notification.worker}_${notification.state}_${recipientEmail || ''}`;
  }

  return `${notification.audienceId}_${notification.worker}_${notification.state}_${notification.timestamp}`;
};

/**
 * Count active (non-completed) notifications in an array
 *
 * @param notifications - Array of notifications to count
 * @returns Count of active notifications
 */
export const countActiveNotifications = (notifications: IWorkflowNotification[]): number => {
  return filterUserFacingNotifications(notifications).filter(isActiveNotification).length;
};

/**
 * Count active notifications the user has NOT seen yet (badge count).
 *
 * The badge counts in-progress workflows, but a missed terminal socket event can
 * leave a notification stuck "active" for up to 72h, so the raw active count gets
 * stuck (e.g. "21") and opening the drawer never clears it. We treat opening the
 * drawer as "seen" (a persisted timestamp) and only count active notifications
 * whose latest activity is newer than that — so opening clears the badge, stale
 * items stay cleared across reloads, and genuinely-new activity still re-badges.
 *
 * @param notifications - All notifications
 * @param seenAt - Epoch ms the user last opened the drawer (0 = never seen)
 * @returns Count of unseen active notifications
 */
export const countUnseenActiveNotifications = (
  notifications: IWorkflowNotification[],
  seenAt: number,
): number => {
  if (!seenAt) return countActiveNotifications(notifications);
  return filterUserFacingNotifications(notifications)
    .filter(isActiveNotification)
    .filter((notification) => {
      const activityAt = new Date(notification.timestamp).getTime();
      // Unknown/invalid timestamp: count it rather than silently hide a real one.
      return Number.isNaN(activityAt) || activityAt > seenAt;
    })
    .length;
};

/**
 * Filter notifications to only include those with displayable content
 *
 * A notification is displayable if it has a title, summary, or message.
 *
 * @param notifications - Array of notifications to filter
 * @returns Filtered array of displayable notifications
 */
export const filterDisplayableNotifications = (notifications: IWorkflowNotification[]): IWorkflowNotification[] => {
  return notifications.filter(notification =>
    notification.standardized?.summary ||
    notification.standardized?.title ||
    notification.message
  );
};

/**
 * Determine visibility for a notification (user-facing vs technical)
 */
export const getNotificationVisibility = (notification: IWorkflowNotification): NotificationVisibility => {
  return notification.standardized?.visibility || notification.visibility || 'user';
};

/**
 * Filter notifications to only include user-facing entries
 */
export const filterUserFacingNotifications = (notifications: IWorkflowNotification[]): IWorkflowNotification[] => {
  return filterDisplayableNotifications(
    notifications.filter((notification) => getNotificationVisibility(notification) === 'user'),
  );
};

/**
 * Sort notifications by timestamp (oldest first)
 *
 * @param notifications - Array of notifications to sort
 * @returns New sorted array (does not mutate original)
 */
const sortNotificationsByTime = (notifications: IWorkflowNotification[]): IWorkflowNotification[] => {
  return [...notifications].sort((a, b) => {
    const timeA = new Date(a.timestamp).getTime();
    const timeB = new Date(b.timestamp).getTime();
    return timeA - timeB; // Ascending order (oldest first)
  });
};

/**
 * Badge variant mapping based on status category
 */
const getStatusBadgeVariant = (
  category: NotificationStatusCategory
): 'success' | 'info' | 'error' | 'warning' => {
  switch (category) {
    case 'completed':
      return 'success';
    case 'processing':
      return 'info';
    case 'failed':
      return 'error';
    case 'pending':
    default:
      return 'warning';
  }
};

/**
 * Grouped notification structure for display
 * Parent notifications can contain child notifications (e.g., emailWorker with emailRecipientWorker children)
 */
interface GroupedNotification {
  /** The parent notification (e.g., emailWorker completed) */
  parent: IWorkflowNotification;
  /** Child notifications grouped under this parent (e.g., individual email recipient results) */
  children: IWorkflowNotification[];
  /** Unique group key for React rendering */
  groupKey: string;
  /** Touch tag for grouping (e.g., "Touch 1", "Touch 2") */
  touchTag: string | null;
  /** Aggregated metrics from children */
  metrics: {
    total: number;
    completed: number;
    failed: number;
    processing: number;
  };
}

/**
 * Workers that produce child notifications that should be grouped under parent
 * These are per-recipient/per-prospect workers that SDRs want to see in detail
 */
const CHILD_WORKERS = [
  'emailRecipientWorker',      // Per-person email results
  'smsRecipientWorker',        // Per-person SMS results
  'linkedinRecipientWorker',   // Per-person LinkedIn outreach results
  'voiceRecipientWorker',      // Per-person voice/dialer results
] as const;

/**
 * Workers that act as parent for child notifications
 * These are the high-level "touch" workers that group per-person results
 */
const PARENT_WORKERS = [
  'emailWorker',       // Email touch (groups emailRecipientWorker children)
  'smsWorker',         // SMS touch
  'linkedinWorker',    // LinkedIn touch
  'voiceWorker',       // Voice/dialer touch
  'dialerWorker',      // Human dialer touch
] as const;

/**
 * Workers that show research/agent results - SDRs want to see these with data
 * These don't group children but should show prominent results
 */
const RESULT_WORKERS = [
  'agentWorker',       // AI Agent research results per prospect
  'prospectWorker',    // Prospect enrichment results
  'researchWorker',    // Company/person research
  'heygenVideoWorker', // Video generation results
] as const;

/**
 * Get audiencePlayId from a notification
 */
const getAudiencePlayId = (notification: IWorkflowNotification): number | null => {
  const id = notification.audiencePlayId
    || notification.raw?.audiencePlayId
    || notification.raw?.audience_play_id;
  return id ? Number(id) : null;
};

/**
 * Get touch tag from a notification (e.g., "Touch 1", "Touch 2")
 */
const getTouchTag = (notification: IWorkflowNotification): string | null => {
  return notification.touchTag
    || notification.raw?.touchTag
    || null;
};

/**
 * Check if a notification is a child worker notification
 */
const isChildWorker = (notification: IWorkflowNotification): boolean => {
  return CHILD_WORKERS.includes(notification.worker as typeof CHILD_WORKERS[number]);
};

/**
 * Check if a notification is a parent worker notification
 */
const isParentWorker = (notification: IWorkflowNotification): boolean => {
  return PARENT_WORKERS.includes(notification.worker as typeof PARENT_WORKERS[number]);
};

/**
 * Check if a notification is a result worker (shows research/agent data)
 */
const isResultWorker = (notification: IWorkflowNotification): boolean => {
  return RESULT_WORKERS.includes(notification.worker as typeof RESULT_WORKERS[number]);
};

/**
 * Group notifications by audiencePlayId for hierarchical display
 *
 * This groups child notifications (emailRecipientWorker) under their parent (emailWorker)
 * based on audiencePlayId. Result workers (agentWorker) are shown prominently.
 * Non-groupable notifications are returned as single-item groups.
 *
 * SDR-focused: Shows plays with results, outreach plays, hides noise
 *
 * @param notifications - Array of notifications to group
 * @returns Array of grouped notifications for display
 */
const groupNotificationsByPlay = (notifications: IWorkflowNotification[]): GroupedNotification[] => {
  // Build a map of audiencePlayId -> notifications
  // NOTE: Consolidation (started → completed) is handled at storage level in NotificationProvider
  // so notifications passed here should already have proper state progression
  const playGroups = new Map<number, IWorkflowNotification[]>();
  const resultNotifications: IWorkflowNotification[] = [];
  const ungrouped: IWorkflowNotification[] = [];

  // First pass: categorize notifications
  for (const notification of notifications) {
    const playId = getAudiencePlayId(notification);

    // Result workers (agentWorker, prospectWorker, etc.) - show prominently
    if (isResultWorker(notification)) {
      resultNotifications.push(notification);
    }
    // Parent/child workers with valid audiencePlayId - group them
    else if (playId && (isParentWorker(notification) || isChildWorker(notification))) {
      if (!playGroups.has(playId)) {
        playGroups.set(playId, []);
      }
      playGroups.get(playId)!.push(notification);
    } else {
      // Other notifications - only include if they have meaningful content
      // Filter out noise (delay, orchestration, etc. should already be filtered by visibility)
      ungrouped.push(notification);
    }
  }

  const result: GroupedNotification[] = [];

  // Add result workers first (AI Agent research, etc.) - SDRs want to see these prominently
  for (const notification of resultNotifications) {
    const playId = getAudiencePlayId(notification);
    result.push({
      parent: notification,
      children: [],
      groupKey: `result_${playId || notification.timestamp}_${notification.worker}`,
      touchTag: getTouchTag(notification) || null,
      metrics: {
        total: 1,
        completed: getStatusCategory(notification) === 'completed' ? 1 : 0,
        failed: getStatusCategory(notification) === 'failed' ? 1 : 0,
        processing: getStatusCategory(notification) === 'processing' || getStatusCategory(notification) === 'pending' ? 1 : 0,
      },
    });
  }

  // Process grouped outreach notifications (email, LinkedIn, voice)
  playGroups.forEach((playNotifications, playId) => {
    // Find parent notification (prefer completed state)
    const parents = playNotifications.filter(isParentWorker);
    const children = playNotifications.filter(isChildWorker);

    // Choose the best parent: prefer completed, then most recent
    const parent = parents.find(p => isCompletedNotification(p))
      || parents.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime())[0];

    if (parent) {
      // Calculate metrics from children
      const metrics = {
        total: children.length,
        completed: children.filter(c => getStatusCategory(c) === 'completed').length,
        failed: children.filter(c => getStatusCategory(c) === 'failed').length,
        processing: children.filter(c => getStatusCategory(c) === 'processing' || getStatusCategory(c) === 'pending').length,
      };

      result.push({
        parent,
        children: children.sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()),
        groupKey: `play_${playId}_${parent.worker}`,
        touchTag: getTouchTag(parent) || getTouchTag(children[0]) || null,
        metrics,
      });
    } else if (children.length > 0) {
      // No parent found, create a synthetic group from children
      // Use first child as representative
      const representative = children[0];
      const metrics = {
        total: children.length,
        completed: children.filter(c => getStatusCategory(c) === 'completed').length,
        failed: children.filter(c => getStatusCategory(c) === 'failed').length,
        processing: children.filter(c => getStatusCategory(c) === 'processing' || getStatusCategory(c) === 'pending').length,
      };

      result.push({
        parent: representative,
        children: children.slice(1).sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()),
        groupKey: `play_${playId}_children`,
        touchTag: getTouchTag(representative) || null,
        metrics,
      });
    }
  });

  // Add other ungrouped notifications as single-item groups
  for (const notification of ungrouped) {
    result.push({
      parent: notification,
      children: [],
      groupKey: `single_${notification.timestamp}_${notification.worker}`,
      touchTag: getTouchTag(notification) || null,
      metrics: {
        total: 1,
        completed: isCompletedNotification(notification) && getStatusCategory(notification) === 'completed' ? 1 : 0,
        failed: getStatusCategory(notification) === 'failed' ? 1 : 0,
        processing: getStatusCategory(notification) === 'processing' || getStatusCategory(notification) === 'pending' ? 1 : 0,
      },
    });
  }

  // Sort by timestamp (most recent first based on parent timestamp)
  return result.sort((a, b) =>
    new Date(b.parent.timestamp).getTime() - new Date(a.parent.timestamp).getTime()
  );
};
